import { useRef, useState } from "react";
import { Box, Stack, StackProps, SvgIcon, Tooltip, Typography } from "@mui/material";
import { CoreIconButton, CoreIconButtonProps } from "./Button";
import i18nPackage from "../../i18n/i18n";
import Copy from "../../mobileInstall/images/copy.svg?react";
import CheckCircle from "./images/check-circle.svg?react";

type CopyMessageLocation = "inplace" | "right" | "left" | "below" | "above";

const copyMessageLocationMap: Record<CopyMessageLocation, StackProps["direction"]> = {
	right: "row-reverse",
	left: "row",
	below: "column-reverse",
	above: "column",
	inplace: "row",
};

export type CoreCopyButtonProps = Omit<CoreIconButtonProps, "variant" | "onCopy"> & {
	text: string;
	onCopy?: () => void;
	/**If passed will try to copy `htmlText` in html form. Will fallback to `text` if failed */
	htmlText?: string;
	/** Where the copied! message shows in relation to the copy button. ex: right means message appears to the right of the copy button.
	 *  `inplace` replaces the copy button. Default is inplace */
	copyMessageLocation?: CopyMessageLocation;
};

export async function handleCopy(input: string, htmlText?: string, onCopy?: () => void) {
	// eslint-disable-next-line @typescript-eslint/no-base-to-string
	const text = input.toString();
	let successful = false;

	/** Some websites' (like D2L's) permissions policies don't allow Clipboard API access, so this function is necessary */
	const fallbackCopy = () => {
		const textArea = document.createElement("textarea");
		textArea.value = text;
		textArea.setAttribute("readonly", "");
		textArea.style.position = "absolute";
		textArea.style.left = "-9999px";
		textArea.style.top = "0";
		document.body.appendChild(textArea);
		textArea.focus();
		textArea.setSelectionRange(0, text.length);
		// eslint-disable-next-line deprecation/deprecation
		return document.execCommand("copy");
	};

	if (navigator.clipboard) {
		try {
			if (htmlText && window.ClipboardItem) {
				const item = new ClipboardItem({
					"text/html": new Blob([htmlText], { type: "text/html" }),
					"text/plain": new Blob([text], { type: "text/plain" }),
				});
				await navigator.clipboard.write([item]);
			} else {
				await navigator.clipboard.writeText(text);
			}
			successful = true;
		} catch {
			successful = fallbackCopy();
		}
	}

	if (successful) {
		onCopy?.();
	}
}

export default function CoreCopyButton({
	text,
	onCopy,
	size,
	htmlText,
	copyMessageLocation = "inplace",
	...buttonProps
}: CoreCopyButtonProps) {
	const [copiedShowing, setCopiedShowing] = useState(false);

	const buttonRef = useRef<HTMLButtonElement>(null);
	const copiedIconRef = useRef<HTMLDivElement>(null);

	const showCopied = () => {
		setCopiedShowing(true);
		setTimeout(() => copiedIconRef.current?.focus());
		setTimeout(() => {
			setCopiedShowing(false);
			if (copiedIconRef.current && document.activeElement === copiedIconRef.current) {
				setTimeout(() => buttonRef.current?.focus());
			}
		}, 3000);
	};

	return copiedShowing && copyMessageLocation === "inplace" ? (
		<Tooltip open={copiedShowing} title={i18nPackage.t("coreComponents.copyButton.copied")}>
			<Box
				tabIndex={0}
				sx={{
					width: 30,
					height: 30,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: 2,
					"&:focus-visible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: 2,
					},
				}}
				ref={copiedIconRef}
			>
				<SvgIcon style={{ color: "#027B1C", fontSize: 24 }}>
					<CheckCircle />
				</SvgIcon>
			</Box>
		</Tooltip>
	) : (
		<Stack
			spacing={1}
			direction={copyMessageLocationMap[copyMessageLocation]}
			alignContent={"center"}
			justifyContent={"center"}
		>
			{copiedShowing && (
				<Stack spacing={0.5} direction={"row"}>
					<SvgIcon style={{ color: "#027B1C", fontSize: 24 }}>
						<CheckCircle />
					</SvgIcon>
					<Typography fontSize={14}>{i18nPackage.t("coreComponents.copyButton.copied")}</Typography>
				</Stack>
			)}
			<CoreIconButton
				{...buttonProps}
				variant="plain"
				size={size}
				onClick={() =>
					handleCopy(text ?? "", htmlText, () => {
						showCopied();
						onCopy?.();
					})
				}
				aria-label={buttonProps["aria-label"] ?? i18nPackage.t("coreComponents.copyButton.copy")}
				ref={buttonRef}
			>
				<SvgIcon style={{ color: "#898989", fontSize: size === "small" ? 16 : 18 }}>
					<Copy />
				</SvgIcon>
			</CoreIconButton>
		</Stack>
	);
}
